import { createFileRoute } from "@tanstack/react-router";
import { ServicePage, serviceHead } from "@/components/site/ServicePage";

const SLUG = "groen-energi";
const TITLE = "Grøn energi, solceller, batterier & smart styring | Elektriker-Elektriker";
const DESC =
  "Solceller, batterilager og smart energistyring til hjem og erhverv i Storkøbenhavn. Producer, lagr og brug din egen strøm, autoriseret installation og hjælp til myndigheder.";

export const Route = createFileRoute("/groen-energi")({
  head: () => serviceHead(SLUG, TITLE, DESC),
  component: () => (
    <ServicePage
      slug={SLUG}
      heroVariant="centered"
      accentColor="oklch(0.72 0.14 145)"
      eyebrow="Grøn energi"
      title="Producer, lagr og brug din egen strøm"
      intro="Vi designer og installerer grønne energiløsninger der gør dig mere selvforsynende, solceller på taget, batteri i bryggerset og smart styring der kører bilen, varmepumpen og varmtvandsbeholderen på den billigste strøm."
      longIntro={
        <>
          <h2>Hvorfor solceller og batteri sammen?</h2>
          <p>
            Solceller alene dækker typisk 30-40% af en husstands eget forbrug, resten skal sælges
            til nettet til markedspris og købes tilbage senere til væsentligt højere pris. Et
            batteri til 5-15 kWh ændrer det regnestykke fundamentalt: du dækker pludselig 60-80% af
            dit eget forbrug, fordi du gemmer dagens overproduktion til aften og nat, hvor du
            faktisk har behovet, og hvor strømmen er dyrest.
          </p>
          <p>
            Vi designer hele løsningen, fra antal og placering af paneler, valg af inverter og
            batteri, til styringen der vurderer prisen time for time og beslutter om bilen skal
            lade fra solen, om batteriet skal lade op, eller om der skal sælges til nettet. Vi
            kender myndighedsdialogen med Energinet og dit netselskab og klarer den for dig.
          </p>
        </>
      }
      bullets={[
        "Solcelleanlæg dimensioneret til dit faktiske forbrug, ikke til taget",
        "Batterilager til at bruge solstrømmen aften og nat",
        "Intelligent styring efter spotpris og produktion",
        "Tilkobling og dokumentation til netselskab og Energinet",
        "Integration med varmepumpe, ladeboks og varmtvandsbeholder",
        "Anerkendte mærker med lang garanti og dansk service",
      ]}
      features={[
        { title: "Solceller", body: "Tag- og facadeanlæg fra anerkendte producenter med produktgaranti og effektgaranti." },
        { title: "Batterilager", body: "5-30 kWh batterier i bryggerset eller garagen, brug din egen strøm hele døgnet." },
        { title: "Smart styring", body: "Styr ladning, varmepumpe og varmtvandsbeholder efter spotpris og solproduktion." },
        { title: "Erhverv", body: "Større anlæg til virksomheder med PPA-aftaler, taglej og driftsovervågning." },
        { title: "Ladestander-integration", body: "Lad din elbil primært på din egen solstrøm, automatisk, uden du tænker over det." },
        { title: "Backup ved strømsvigt", body: "Et batteri med ø-drift kan holde lyset, fryseren og internettet i gang hvis strømmen går." },
      ]}
      process={[
        { title: "Forbrugsanalyse", body: "Vi henter data fra Eloverblik og finder dit faktiske forbrug time for time over et helt år." },
        { title: "Design", body: "Vi tegner anlægget, beregner produktion, dækningsgrad og økonomi, og giver dig flere muligheder." },
        { title: "Installation", body: "Typisk 2-5 dage afhængigt af størrelse. Vi koordinerer både tag, el og myndigheder." },
        { title: "Idriftsættelse", body: "Vi sætter anlægget i drift, registrerer det og lærer dig at læse app og udnytte styringen." },
      ]}
      faq={[
        { q: "Kan det betale sig?", a: "Tilbagebetalingstiden ligger typisk på 7-12 år for et komplet sol- og batterianlæg. Anlægget holder typisk 25+ år, så langt størstedelen af levetiden er ren besparelse." },
        { q: "Skal jeg have batteri?", a: "Det giver mest mening hvis du har stort aften-/nat-forbrug, en elbil, eller hvis du vil have backup ved strømsvigt. For mange små familier er det rentabelt med batteri; for ren dagforbrug er det nogle gange bedre at investere mere i paneler." },
        { q: "Hjælper I med tilladelser?", a: "Ja, vi tager hele dialogen med netselskabet og registrerer anlægget hos Energinet på dine vegne." },
        { q: "Hvor stor en investering?", a: "Et typisk villa-anlæg (8-10 kWp solceller + 10 kWh batteri) ligger på 110.000-170.000 kr. inkl. moms og installation. Vi giver fast pris efter besigtigelse." },
        { q: "Skal taget kunne bære?", a: "Næsten alle danske tage kan bære moderne solcelleanlæg, vi tjekker konstruktionen før vi monterer og laver evt. forstærkning hvis nødvendigt." },
        { q: "Hvilke mærker bruger I?", a: "Vi arbejder med anerkendte producenter som SolarEdge, Huawei, Sungrow, BYD og Sonnen, valget afhænger af dit projekt, ikke af hvad vi har på lager." },
      ]}
      related={[
        { to: "/ladestandere", title: "Ladestandere", body: "Lad bilen direkte fra dit eget tag." },
        { to: "/varmepumper", title: "Varmepumper", body: "Den største energi-løsning starter ofte med varmen." },
        { to: "/energioptimering", title: "Energioptimering", body: "Sænk forbruget før du producerer mere strøm." },
      ]}
    />
  ),
});
